import * as React from "react";

import { detectSurface, type Surface } from "@/lib/surface-detect";

// App-wide access to the classified install surface (Story 7.1). Mirrors auth-context:
// a typed Provider + a `useSurface()` hook that fails closed (throws) when used outside
// the Provider, and an exported context so tests can supply a controlled value without
// standing up the Provider.

export interface SurfaceContextValue {
  surface: Surface;
}

export const SurfaceContext = React.createContext<SurfaceContextValue | null>(null);

export function SurfaceProvider({ children }: { children: React.ReactNode }) {
  // Detect once on mount (lazy initializer — reads globals exactly once at first render).
  const [surface, setSurface] = React.useState<Surface>(() => detectSurface());

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    // A surface can flip at runtime: a clinician who launches the installed PWA from the
    // home screen moves Safari-proper → pwa-proper, which fires the display-mode media
    // query. Re-classify on that change so the gate clears without a reload.
    // matchMedia can throw on some locked-down WKWebViews — bail out of the listener
    // wiring rather than letting it escape the effect (initial detection already ran).
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia("(display-mode: standalone)");
    } catch {
      return;
    }
    const onChange = () => setSurface(detectSurface());
    // Older WebKit only exposes the deprecated addListener; guard for both.
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  const value = React.useMemo<SurfaceContextValue>(() => ({ surface }), [surface]);

  return <SurfaceContext.Provider value={value}>{children}</SurfaceContext.Provider>;
}

export function useSurface(): SurfaceContextValue {
  const ctx = React.useContext(SurfaceContext);
  if (!ctx) {
    throw new Error("useSurface must be used inside <SurfaceProvider>");
  }
  return ctx;
}
